import React, { useEffect, useRef, useState } from 'react';

import { TextField } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import styled from '@material-ui/core/styles/styled';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import startOfDay from 'date-fns/startOfDay';
import subMonths from 'date-fns/subMonths';
import { useTranslation } from 'react-i18next';

import { CustomButton } from '../../../../../../components/UI/CustomButton';
import { colorsPalette } from '../../../../../../theme';
import StyledPicker from '../StyledPicker/StyledPicker';
import { useStyles } from './styles';

const DATE_FORMAT = 'dd/MM/yyyy';

export const isInPeriod = (current, min, max) => {
  let valid = true;

  if (!current) {
    return valid;
  }

  const minDiff = min ? differenceInCalendarDays(current, min) : 0;
  const maxDiff = max ? differenceInCalendarDays(current, max) : 0;

  if (min && minDiff <= 0) {
    valid = false;
  }

  if (max && maxDiff > 0) {
    valid = false;
  }

  return valid;
};

const PickerPare = ({ isDesktop, closeHandler, submitHandler, currentDate }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [firstMonth, setFirstMonth] = useState(subMonths(currentDate, 1));
  const [secondMonth, setSecondMonth] = useState(currentDate);

  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [startInputValue, setStartInputValue] = useState('');
  const [endInputValue, setEndInputValue] = useState('');
  const [errorStart, setErrorStart] = useState(false);
  const [errorEnd, setErrorEnd] = useState(false);

  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);

  startDateRef.current = startDate;
  endDateRef.current = endDate;

  useEffect(() => {
    if (startInputValue) {
      const parsed = parse(startInputValue, DATE_FORMAT, currentDate);
      const correctEnd = endDateRef.current ? endOfDay(endDateRef.current) : null;
      if (isValid(parsed) && isInPeriod(parsed, null, correctEnd)) {
        setErrorStart(false);
        setStartDate(parsed);
        startDateRef.current = parsed;
      } else {
        setErrorStart(true);
      }
    }

    if (endInputValue) {
      const parsed = endOfDay(parse(endInputValue, DATE_FORMAT, currentDate));
      if (isValid(parsed) && isInPeriod(parsed, startDateRef.current, endOfDay(currentDate))) {
        setErrorEnd(false);
        setEndDate(parsed);
        endDateRef.current = parsed;
      } else {
        setErrorEnd(true);
      }
    }
  }, [startInputValue, endInputValue, startDateRef, endDateRef, currentDate]);

  const handleStartPicker = (d) => {
    setStartDate(d);
    setStartInputValue(d ? format(d, DATE_FORMAT) : '');
  };

  const handleEndPicker = (d) => {
    setEndDate(d);
    setEndInputValue(d ? format(d, DATE_FORMAT) : '');
  };

  const handleStartInput = (e) => {
    const val = e.target.value;
    if (val === '') setStartDate(undefined);
    setStartInputValue(val);
  };

  const handleEndInput = (e) => {
    const val = e.target.value;
    if (val === '') setEndDate(undefined);
    setEndInputValue(val);
  };

  return (
    <Wrapper data-testid="pickerPareWrapper">
      <Pickers>
        {isDesktop && (
          <>
            <StyledPicker
              currentMonth={firstMonth}
              start={startDate}
              end={endDate}
              startHandler={handleStartPicker}
              endHandler={handleEndPicker}
              currentMonthHandler={setFirstMonth}
            />
            <PickersDivider />
          </>
        )}
        <StyledPicker
          currentMonth={secondMonth}
          start={startDate}
          end={endDate}
          startHandler={handleStartPicker}
          endHandler={handleEndPicker}
          currentMonthHandler={setSecondMonth}
        />
      </Pickers>
      <PickerForm>
        <RangeFields>
          <TextField
            className={classes.rangeField}
            id="from"
            name="from"
            variant="outlined"
            label={t('input.placeholder.from')}
            value={startInputValue}
            onChange={handleStartInput}
            placeholder={format(currentDate, 'dd/MM/yyyy')}
            error={errorStart}
            inputProps={{ 'data-testid': 'pickerFrom' }}
          />
          <Divider>-</Divider>
          <TextField
            className={classes.rangeField}
            id="to"
            name="to"
            variant="outlined"
            label={t('input.placeholder.to')}
            value={endInputValue}
            onChange={handleEndInput}
            placeholder={format(currentDate, 'dd/MM/yyyy')}
            error={errorEnd}
            inputProps={{ 'data-testid': 'pickerTo' }}
          />
        </RangeFields>
        <Buttons>
          <CustomButton
            data-testid="cancel"
            btnClass={classes.cancelButton}
            color="secondary"
            onClick={closeHandler}
          >
            {t('button.cancel')}
          </CustomButton>
          <CustomButton
            data-testid="apply"
            disabled={!startDate || !endDate || errorStart || errorEnd}
            btnClass={classes.applyButton}
            onClick={() => {
              // @ts-ignore
              submitHandler(startOfDay(startDate), endOfDay(endDate));
            }}
          >
            {t('button.apply')}
          </CustomButton>
        </Buttons>
      </PickerForm>
    </Wrapper>
  );
};

export default PickerPare;

const Wrapper = styled('div')({
  width: '100%',
});
const Pickers = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    '& .DayPicker:first-child': {
      display: 'none',
    },
  },
  [theme.breakpoints.up('lg')]: {
    '& .DayPicker:first-child': {
      display: 'inline-block',
    },
  },
}));
const PickerForm = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px 18px',
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    padding: '44px 38px 34px',
  },
}));
const RangeFields = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 36,
  [theme.breakpoints.up('md')]: {
    marginRight: 'auto',
  },
  [theme.breakpoints.up('lg')]: {
    marginBottom: 0,
  },
}));
const Buttons = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: 330,
  [theme.breakpoints.up('lg')]: {
    width: 'initial',
    marginLeft: 18,
  },
}));
const Divider = styled('span')(({ theme }) => ({
  margin: '0 8px',
  [theme.breakpoints.up('lg')]: {
    margin: '0 18px',
  },
}));
const PickersDivider = styled('span')(({ theme }) => ({
  width: 1,
  height: 230,
  margin: 'auto 24px 16px',
  background: fade(colorsPalette.white, 0.16),
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
  [theme.breakpoints.up('lg')]: {
    display: 'inline',
  },
}));
